import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import Footer from '../../components/Footer';
import { AuthContext, AppSettingsContext } from '../../App';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  BookOpen, Calendar, Eye, ArrowLeft, ArrowRight, User, 
  Share2, Facebook, Twitter, Linkedin, Link as LinkIcon, Rocket,
  Clock, TrendingUp
} from 'lucide-react';
import { useDateFormat } from '../../contexts/DateFormatContext';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const { formatDate } = useDateFormat();
  const [article, setArticle] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
    fetchRecentArticles();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog/articles/${slug}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentArticles = async () => {
    try {
      const response = await axios.get(`${API}/blog/recent`, { params: { limit: 4 } });
      setRecentArticles(response.data.filter(a => a.slug !== slug).slice(0, 3));
    } catch (error) {
      console.error('Error fetching recent articles:', error);
    }
  };

  // Use API endpoint for sharing (serves proper OG tags for crawlers)
  // In production, use the production domain; in preview, use the backend URL
  const getShareBaseUrl = () => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    // If we're on the production domain, use it; otherwise use backend URL for preview
    if (hostname === 'bettingtracker.pro' || hostname === 'www.bettingtracker.pro') {
      return 'https://bettingtracker.pro';
    }
    return process.env.REACT_APP_BACKEND_URL || '';
  };
  const shareBaseUrl = getShareBaseUrl();
  // Use the actual public blog URL for sharing (not an API endpoint)
  const shareUrl = article?.slug ? `${shareBaseUrl}/blog/${article.slug}` : (typeof window !== 'undefined' ? window.location.href : '');
  const shareTitle = article?.title || 'Blog Article';

  const shareLinks = [
    {
      name: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      color: 'hover:bg-blue-600'
    },
    {
      name: 'Twitter',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
      color: 'hover:bg-sky-500'
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`,
      color: 'hover:bg-blue-700'
    }
  ];

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
  };

  const ShareButtons = () => (
    <div className="flex items-center gap-2">
      <span className="text-gray-400 text-sm flex items-center gap-1">
        <Share2 className="w-4 h-4" />
        Share:
      </span>
      {shareLinks.map(link => (
        <a
          key={link.name}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`p-2 rounded-full bg-white/10 text-gray-300 ${link.color} transition-colors`}
          title={`Share on ${link.name}`}
        >
          <link.icon className="w-4 h-4" />
        </a>
      ))}
      <button
        onClick={copyLink}
        className="p-2 rounded-full bg-white/10 text-gray-300 hover:bg-green-600 transition-colors"
        title="Copy link"
      >
        <LinkIcon className="w-4 h-4" />
      </button>
    </div>
  );

  const CTACard = () => (
    <Card className="glass border-green-500/30 overflow-hidden my-8">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500" />
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-center">
              <Rocket className="w-8 h-8 text-green-400" />
            </div>
          </div>
          <div className="flex-grow text-center md:text-left">
            <h3 className="text-xl font-bold text-white mb-2">Ready to Improve Your Betting?</h3>
            <p className="text-gray-400 text-sm">
              Track your bets, get AI-powered analysis, and join our community of successful bettors.
            </p>
          </div>
          <div className="flex-shrink-0">
            <Link to="/signup">
              <Button className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white">
                Get Started Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Calculate reading time
  const wordCount = article?.content?.split(/\s+/).length || 0;
  const readingTime = Math.ceil(wordCount / 200);

  const pageContent = (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
            <p className="text-gray-400 mt-4">Loading article...</p>
          </div>
        ) : !article ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Article Not Found</h3>
            <p className="text-gray-400 mb-4">The article you're looking for doesn't exist.</p>
            <Link to="/blog">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Back Link */}
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            {/* Article Header */}
            <article>
              <header className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    {article.category}
                  </span>
                  {article.tags?.map(tag => (
                    <span key={tag} className="px-2 py-1 rounded-full text-xs bg-white/10 text-gray-400">
                      {tag}
                    </span>
                  ))}
                </div>
                
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {article.title}
                </h1>
                
                <p className="text-xl text-gray-300 mb-6">
                  {article.excerpt}
                </p>
                
                <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-white/10">
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(article.published_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {readingTime} min read
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {article.views} views
                    </span>
                  </div>
                  <ShareButtons />
                </div>
              </header>

              {/* Featured Image */}
              {article.featured_image && (
                <div className="mb-8 rounded-xl overflow-hidden">
                  <img 
                    src={article.featured_image} 
                    alt={article.title}
                    className="w-full h-auto"
                  />
                </div>
              )}

              {/* Article Content */}
              <div className="prose prose-invert prose-lg max-w-none mb-8">
                <ReactMarkdown
                  components={{
                    h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-white mt-8 mb-4" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-white mt-6 mb-3" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-white mt-5 mb-2" {...props} />,
                    p: ({node, ...props}) => <p className="text-gray-300 mb-4 leading-relaxed" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal list-inside text-gray-300 mb-4 space-y-2" {...props} />,
                    li: ({node, ...props}) => <li className="text-gray-300" {...props} />,
                    strong: ({node, ...props}) => <strong className="text-white font-semibold" {...props} />,
                    em: ({node, ...props}) => <em className="text-blue-400" {...props} />,
                    blockquote: ({node, ...props}) => (
                      <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-400 my-4" {...props} />
                    ),
                    a: ({node, ...props}) => (
                      <a className="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer" {...props} />
                    ),
                    code: ({node, inline, ...props}) => (
                      inline 
                        ? <code className="bg-white/10 px-1 py-0.5 rounded text-green-400" {...props} />
                        : <code className="block bg-white/10 p-4 rounded-lg overflow-x-auto text-green-400" {...props} />
                    ),
                  }}
                >
                  {article.content}
                </ReactMarkdown>
              </div>

              {/* Share Section */}
              <div className="flex items-center justify-between py-6 border-t border-b border-white/10 mb-8">
                <span className="text-gray-400">Share this article:</span>
                <ShareButtons />
              </div>
            </article>

            {/* CTA for guests */}
            {!user && <CTACard />}

            {/* Recent Articles */}
            {recentArticles.length > 0 && (
              <div className="mt-12">
                <h2 className="text-2xl font-bold text-white mb-6">More Articles</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {recentArticles.map(recent => (
                    <Link key={recent.id} to={`/blog/${recent.slug}`} className="group">
                      <Card className="glass-dark border-white/10 overflow-hidden h-full hover:border-blue-500/50 transition-all">
                        <CardContent className="p-4">
                          <span className="text-xs text-blue-400 mb-2 block">{recent.category}</span>
                          <h3 className="text-white font-semibold group-hover:text-blue-400 transition-colors line-clamp-2">
                            {recent.title}
                          </h3>
                          <p className="text-gray-500 text-xs mt-2">
                            {formatDate(recent.published_at)}
                          </p>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // If user is logged in, use Layout component
  if (user) {
    return (
      <Layout>
        {pageContent}
      </Layout>
    );
  }

  // Public view with marketing-style navigation (like BecomeTipster)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* Public Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-dark border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              {appSettings?.app_logo_url ? (
                <img 
                  src={appSettings.app_logo_url} 
                  alt={appSettings?.app_name || 'BettingTracker'} 
                  className="h-10 max-w-[200px] object-contain"
                />
              ) : (
                <>
                  <TrendingUp className="w-8 h-8 text-blue-400" />
                  <span className="text-lg sm:text-xl font-bold whitespace-nowrap">{appSettings?.app_name || 'BettingTracker'}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Link to="/blog" className="text-gray-300 hover:text-white transition-colors">
                Blog
              </Link>
              <Button 
                onClick={() => navigate('/login')} 
                variant="ghost"
                className="text-gray-300 hover:text-white"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/signup')} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content with top padding for fixed nav */}
      <div className="pt-16">
        {pageContent}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BlogArticle;
